import { z } from "zod";

import { providerCredentialRefSchema, providerCredentialScope } from "./provider-credentials";

export const documentParserConnectionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("datalab"),
    credential: providerCredentialRefSchema,
  }),
  z.object({
    type: z.literal("extend"),
    credential: providerCredentialRefSchema,
  }),
]);

export type DocumentParserConnection = z.infer<typeof documentParserConnectionSchema>;

export const documentParserPolicyDocumentSchema = z.object({
  connections: z.record(z.string().min(1), documentParserConnectionSchema).optional(),
  chain: z.array(z.string().min(1)).optional(),
});

export type DocumentParserPolicyDocument = z.infer<typeof documentParserPolicyDocumentSchema>;

export function parseDocumentParserPolicy(
  value: unknown,
  scope: "platform" | "workspace",
): DocumentParserPolicyDocument {
  const document = documentParserPolicyDocumentSchema.parse(value);
  const issues: string[] = [];
  for (const [name, connection] of Object.entries(document.connections ?? {})) {
    const credentialScope = providerCredentialScope(connection.credential);
    if (connection.credential.source === "none" || connection.credential.source === "ambient") {
      issues.push(
        `connections.${name}.credential: ${connection.type} requires an API key reference`,
      );
    } else if (scope === "platform" && credentialScope !== "deployment") {
      issues.push(
        `connections.${name}.credential: deployment parser connections must use AWS Secrets Manager or Azure Key Vault`,
      );
    } else if (scope === "workspace" && credentialScope !== "workspace") {
      issues.push(
        `connections.${name}.credential: workspace parser connections must use a workspace provider credential`,
      );
    }
  }
  const chainTypes = (document.chain ?? [])
    .map((name) => document.connections?.[name]?.type)
    .filter((type): type is "datalab" | "extend" => Boolean(type));
  if (chainTypes.filter((type) => type === "datalab").length > 1) {
    issues.push("chain: only one Datalab connection may be selected in the first release");
  }
  if (chainTypes.filter((type) => type === "extend").length > 1) {
    issues.push("chain: only one Extend connection may be selected in the first release");
  }
  if (
    chainTypes.indexOf("extend") >= 0 &&
    chainTypes.indexOf("datalab") > chainTypes.indexOf("extend")
  ) {
    issues.push("chain: Datalab must precede Extend in the first release");
  }
  if (issues.length > 0) throw new Error(issues.join("; "));
  return document;
}
